import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  ICustomIdInputFieldSettingsModel,
  IFormbuilderFieldTypeConfiguration,
} from '../../../models/form-builder-types-configurations.model';
import { FormBuilderService } from '../../../services/form-builder.service';
import {
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
  populatesPropsParseIntoSettingsFormModel,
  populatesPropsUpdateFromSettingsFormModel,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import {
  parseInvalidGenericInputValidatorParseFieldIntoModel,
  parseRequiredValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  parseConditionalValidatorsModelFromFieldIntoModel,
  parseUtilValidatorsModelFromFieldIntoModel,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-parse-to-model.util';
import {
  setInvalidGenericInputFormDataIntoField,
  setRequiredValidatorUpdateFieldFromModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import {
  setConditionalExpressionFromModel,
  setUtilConditionalExpressionFromModel,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';

interface IOtpDataFetchFieldContentConfig {
  url?: string;
  useBaseUrl?: boolean;
  endpointCode?: string;
  otpLength?: number;
  populates?: [];
  payloadKey?: string;
  allowOtpNumbersOnly?: boolean;
  inputRegexValueValidation?: string;
  inputRegexValueValidationMessage?: string;
  messagePendingRequestOtp?: string;
  messageOtpRequired?: string;
  messageOtpVerificationPending?: string;
  messageDataFetchPending?: string;
  messageDataFetchComplete?: string;
  mixpanelEventAnalytics?: boolean;
}

export interface IOtpDataFetchConfigurationFormModel
  extends ICustomIdInputFieldSettingsModel {
  otpDataFetchFieldContentConfig?: IOtpDataFetchFieldContentConfig;
  defaultValue?: string;
}

export const otpDataFetchInputGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...fieldsBaseConfigurationFormModel(),
        url: '',
        useBaseUrl: true,
        endpointCode: '',
        otpLength: 0,
        populates: [],
        payloadKey: '',
        allowOtpNumbersOnly: false,
        inputRegexValueValidation: '',
        inputRegexValueValidationMessage: '',
        messagePendingRequestOtp: '',
        messageOtpRequired: '',
        messageOtpVerificationPending: '',
        messageDataFetchPending: '',
        messageDataFetchComplete: '',
      },
      formlyForm: [
        ...getOtpDataFetchInputFieldSettingsConfig(formBuilderServiceInstance),
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        {
          key: 'defaultValue',
          type: 'input',
          props: {
            label: 'Default value',
            placeholder: 'Enter default value',
          },
          validation: {
            messages: {
              maxLength: 'Default value is longer than Input Length value',
            },
          },
          expressions: {
            'props.maxLength': field => {
              return (
                field.parent?.model?.otpDataFetchFieldContentConfig
                  ?.otpDataFetchLength ?? null
              );
            },
          },
        },
        getPopulateRelativeToRootFieldConfig(),
        ...getPopulatesPropsSettingsConfigFormly(
          formBuilderServiceInstance,
          []
        ),
      ],
    },
  };
};

export const getOtpDataFetchInputFieldSettingsConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  return [
    {
      key: 'otpDataFetchFieldContentConfig',
      id: 'otpDataFetchFieldContentConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        dragDisabled: false,
      },
      fieldGroup: [
        {
          key: 'url',
          type: 'input',
          props: {
            label: 'API URL',
            required: false,
            placeholder: 'Enter API URL',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'endpointCode',
          type: 'input',
          props: {
            label: 'Endpoint Code',
            required: false,
            placeholder: 'Enter Endpoint Code',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'payloadKey',
          type: 'input',
          props: {
            label: 'Payload Key',
            required: true,
            placeholder: 'Enter Payload Key',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'inputRegexValueValidation',
          type: 'input',
          props: {
            label: 'Input Regex Value Validation',
            placeholder: 'Enter Regex Validation for the input(If any)',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'inputRegexValueValidationMessage',
          type: 'input',
          props: {
            label: 'Regex Value Validation Message',
            placeholder: 'Enter Error message for Regex Validation',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return field?.parent?.model?.inputRegexValueValidation;
            },
          },
        },
        {
          key: 'messagePendingRequestOtp',
          type: 'input',
          props: {
            label: 'Pending request otp message',
            placeholder: 'Enter custom pending request otp message',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'messageOtpRequired',
          type: 'input',
          props: {
            label: 'Otp required message',
            placeholder: 'Enter custom otp required message',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'messageOtpVerificationPending',
          type: 'input',
          props: {
            label: 'Pending otp verification message',
            placeholder: 'Enter custom pending otp verification message',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'messageDataFetchPending',
          type: 'input',
          props: {
            label: 'Pending data fetch message',
            placeholder: 'Enter custom pending data fetch message',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'messageDataFetchComplete',
          type: 'input',
          props: {
            label: 'Data fetch complete message',
            placeholder: 'Enter data fetch complete success message',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'useBaseUrl',
          type: 'checkbox',
          props: {
            label: 'Use Base URL',
          },
        },
        {
          key: 'mixpanelEventAnalytics',
          type: 'checkbox',
          defaultValue: false,
          props: {
            label: 'Enable MixPanel Analytics',
            formCheck: 'switch',
          },
        },
      ],
    },
  ];
};

export const otpDataFetchFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const otpDataFetchFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getPopulateRelativeToRootFieldConfig = (): FormlyFieldConfig => {
  return {
    key: 'populateRelativeToRootForm',
    type: EIremboFormlyFieldTypes.checkbox,
    defaultValue: false,
    props: {
      label: 'Enable populating of fields in all form sections',
      description:
        'On default, you can only populate sibling fields. Enable this to allow population of data into fields in other form sections.',
    },
  };
};

export const getPopulatesPropsSettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService,
  dataFieldProps: string[]
): FormlyFieldConfig[] => {
  return [
    {
      key: 'populates',
      type: EIremboFormlyFieldTypes.customexpansionrepeater,
      wrappers: ['custom-expansion-panel-with-icon'],
      props: {
        label: 'Populate Fields',
        hideLabel: true,
        required: false,
        titleFieldName: 'targetKey',
        addText: 'Add Fields to populate',
      },
      fieldArray: {
        fieldGroup: [
          {
            key: 'valueKey',
            type: dataFieldProps.length
              ? EIremboFormlyFieldTypes.customdropdown
              : EIremboFormlyFieldTypes.input,
            props: {
              options: dataFieldProps.map((prop: string) => {
                return { label: prop, value: prop };
              }),
              placeholder: 'Enter key of ID data field',
              bindLabel: 'label',
              bindValue: 'value',
              hideHeader: true,
            },
          },
          {
            key: 'targetKey',
            type: EIremboFormlyFieldTypes.customdropdown,
            props: {
              placeholder: 'Select target Field to populate data',
              options:
                formBuilderServiceInstance.getConfiguredFieldSiblingsFieldLabelAndDetails(
                  true,
                  true
                ),
              multiple: false,
              bindLabel: 'label',
              bindValue: 'value',
              required: false,
              hideHeader: true,
            },
            expressions: {
              hide: 'field?.parent?.parent?.parent?.model?.populateRelativeToRootForm',
            },
          },
          {
            key: 'targetKey',
            type: EIremboFormlyFieldTypes.customdropdown,
            props: {
              placeholder: 'Select target Field to populate data',
              options: formBuilderServiceInstance
                .getAllFormFieldLabelAndDetails(true, true)
                .map(fieldInfo => {
                  return {
                    label: fieldInfo.label,
                    value: `${fieldInfo.fieldRefPath}`.replace(/\?/g, ''),
                  };
                }),
              multiple: false,
              bindLabel: 'label',
              bindValue: 'value',
              required: false,
              hideHeader: true,
            },
            expressions: {
              hide: '!field?.parent?.parent?.parent?.model?.populateRelativeToRootForm',
            },
          },
        ],
      },
    },
  ];
};

export const getPayloadKeySettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  return [
    {
      key: 'payloadKey',
      type: 'input',
      props: {
        label: 'Payload Key',
        required: true,
        placeholder: 'Enter Payload Key',
      },
      validation: {
        messages: {
          required: 'This field is required',
        },
      },
    },
  ];
};

export const otpDataFetchFieldParseIntoSettingsFormModel = (
  otpDataFetchField: FormlyFieldConfig
): IOtpDataFetchConfigurationFormModel => {
  const model: IOtpDataFetchConfigurationFormModel =
    populatesPropsParseIntoSettingsFormModel(otpDataFetchField);
  model.otpDataFetchFieldContentConfig = {
    url: otpDataFetchField.props?.['url'],
    useBaseUrl: otpDataFetchField.props?.['useBaseUrl'],
    endpointCode: otpDataFetchField.props?.['endpointCode'],
    payloadKey: otpDataFetchField.props?.['payloadKey'],
    otpLength: otpDataFetchField.props?.['otpLength'],
    populates: otpDataFetchField.props?.['populates'],
    allowOtpNumbersOnly: otpDataFetchField.props?.['allowOtpNumbersOnly'],
    inputRegexValueValidation:
      otpDataFetchField.props?.['inputRegexValueValidation'],
    inputRegexValueValidationMessage:
      otpDataFetchField.props?.['inputRegexValueValidationMessage'],
    messagePendingRequestOtp:
      otpDataFetchField.props?.['messagePendingRequestOtp'],
    messageOtpRequired: otpDataFetchField.props?.['messageOtpRequired'],
    messageOtpVerificationPending:
      otpDataFetchField.props?.['messageOtpVerificationPending'],
    messageDataFetchPending:
      otpDataFetchField.props?.['messageDataFetchPending'],
    messageDataFetchComplete:
      otpDataFetchField.props?.['messageDataFetchComplete'],
    mixpanelEventAnalytics: otpDataFetchField.props?.['mixpanelEventAnalytics'],
  };
  model.defaultValue = otpDataFetchField.defaultValue || null;
  return model;
};

export const otpDataFetchFieldUpdateFromSettingsFormModel = (
  model: IOtpDataFetchConfigurationFormModel,
  otpDataFetchField: FormlyFieldConfig
): FormlyFieldConfig => {
  otpDataFetchField = baseFieldUpdateFromSettingsFormModel(
    model,
    otpDataFetchField
  );
  otpDataFetchField = populatesPropsUpdateFromSettingsFormModel(
    model,
    otpDataFetchField
  );

  if (otpDataFetchField.props) {
    otpDataFetchField.props['url'] = model.otpDataFetchFieldContentConfig?.url;
    otpDataFetchField.props['useBaseUrl'] =
      model.otpDataFetchFieldContentConfig?.useBaseUrl;
    otpDataFetchField.props['endpointCode'] =
      model.otpDataFetchFieldContentConfig?.endpointCode;
    otpDataFetchField.props['populates'] =
      model.otpDataFetchFieldContentConfig?.populates;
    otpDataFetchField.props['payloadKey'] =
      model.otpDataFetchFieldContentConfig?.payloadKey;
    otpDataFetchField.props['allowOtpNumbersOnly'] =
      model.otpDataFetchFieldContentConfig?.allowOtpNumbersOnly;
    otpDataFetchField.props['otpLength'] =
      model.otpDataFetchFieldContentConfig?.otpLength;
    otpDataFetchField.props['inputRegexValueValidation'] =
      model.otpDataFetchFieldContentConfig?.inputRegexValueValidation;
    otpDataFetchField.props['inputRegexValueValidationMessage'] =
      model.otpDataFetchFieldContentConfig?.inputRegexValueValidationMessage;
    otpDataFetchField.props['messagePendingRequestOtp'] =
      model.otpDataFetchFieldContentConfig?.messagePendingRequestOtp;
    otpDataFetchField.props['messageOtpRequired'] =
      model.otpDataFetchFieldContentConfig?.messageOtpRequired;
    otpDataFetchField.props['messageOtpVerificationPending'] =
      model.otpDataFetchFieldContentConfig?.messageOtpVerificationPending;
    otpDataFetchField.props['messageDataFetchPending'] =
      model.otpDataFetchFieldContentConfig?.messageDataFetchPending;
    otpDataFetchField.props['messageDataFetchComplete'] =
      model.otpDataFetchFieldContentConfig?.messageDataFetchComplete;
    otpDataFetchField.props['mixpanelEventAnalytics'] =
      model.otpDataFetchFieldContentConfig?.mixpanelEventAnalytics;
  } else {
    otpDataFetchField.props = {
      url: model.otpDataFetchFieldContentConfig?.url,
      useBaseUrl: model.otpDataFetchFieldContentConfig?.useBaseUrl,
      endpointCode: model.otpDataFetchFieldContentConfig?.endpointCode,
      otpLength: model.otpDataFetchFieldContentConfig?.otpLength,
      payloadKey: model.otpDataFetchFieldContentConfig?.payloadKey,
      allowOtpNumbersOnly:
        model.otpDataFetchFieldContentConfig?.allowOtpNumbersOnly,
      inputRegexValueValidation:
        model.otpDataFetchFieldContentConfig?.inputRegexValueValidation,
      inputRegexValueValidationMessage:
        model.otpDataFetchFieldContentConfig?.inputRegexValueValidationMessage,
      messagePendingRequestOtp:
        model.otpDataFetchFieldContentConfig?.messagePendingRequestOtp,
      messageOtpRequired:
        model.otpDataFetchFieldContentConfig?.messageOtpRequired,
      messageOtpVerificationPending:
        model.otpDataFetchFieldContentConfig?.messageOtpVerificationPending,
      messageDataFetchPending:
        model.otpDataFetchFieldContentConfig?.messageDataFetchPending,
      messageDataFetchComplete:
        model.otpDataFetchFieldContentConfig?.messageDataFetchComplete,
      mixpanelEventAnalytics:
        model.otpDataFetchFieldContentConfig?.mixpanelEventAnalytics,
    };
  }

  otpDataFetchField.defaultValue = model.defaultValue?.trim() ?? null;

  return otpDataFetchField;
};

export const otpDataFetchFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseInvalidGenericInputValidatorParseFieldIntoModel(model, field);
  model = parseConditionalValidatorsModelFromFieldIntoModel(model, field);
  model = parseUtilValidatorsModelFromFieldIntoModel(model, field);

  return model;
};

export const otpDataFetchFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  otpDataFetchInput: FormlyFieldConfig
): FormlyFieldConfig => {
  otpDataFetchInput = setRequiredValidatorUpdateFieldFromModel(
    model,
    otpDataFetchInput
  );
  otpDataFetchInput = setInvalidGenericInputFormDataIntoField(
    model,
    otpDataFetchInput
  );
  otpDataFetchInput = setConditionalExpressionFromModel(
    model,
    otpDataFetchInput
  );
  otpDataFetchInput = setUtilConditionalExpressionFromModel(
    model,
    otpDataFetchInput
  );

  return otpDataFetchInput;
};
